<template>
  <div class="configurator-migration-modal">
    <!-- Header -->
    <div class="migration-header">
      <div class="header-icon">
        <BaseIcon name="refresh" size="24" />
      </div>
      <div class="header-content">
        <h3>Configurator Migratie Vereist</h3>
        <p>
          Er zijn wijzigingen gedetecteerd in de producten die gebruikt worden door
          <strong>{{ configuratorName }}</strong>
          . Kies hoe je wilt doorgaan.
        </p>
      </div>
    </div>

    <!-- Migration Summary -->
    <div class="migration-summary">
      <div class="summary-stats">
        <div class="stat-item">
          <span class="stat-number">{{ migrationData.changes.length }}</span>
          <span class="stat-label">Wijzigingen</span>
        </div>
        <div class="stat-item" v-if="errorCount > 0">
          <span class="stat-number error">{{ errorCount }}</span>
          <span class="stat-label">Kritiek</span>
        </div>
        <div class="stat-item" v-if="warningCount > 0">
          <span class="stat-number warning">{{ warningCount }}</span>
          <span class="stat-label">Waarschuwingen</span>
        </div>
      </div>
    </div>

    <!-- Changes Detail -->
    <div class="changes-detail">
      <h4>Gedetecteerde Wijzigingen</h4>
      <div class="changes-list">
        <div
          v-for="change in migrationData.changes"
          :key="change.productId"
          class="change-item"
          :class="`change-item--${change.severity}`"
        >
          <div class="change-icon">
            <BaseIcon
              :name="getChangeIcon(change.type)"
              size="20"
              :class="`icon--${change.severity}`"
            />
          </div>

          <div class="change-content">
            <div class="change-header">
              <h5>{{ change.productName || change.productId }}</h5>
              <span class="change-type">{{ getChangeTypeLabel(change.type) }}</span>
            </div>

            <div class="change-description">
              <p v-if="change.message">{{ change.message }}</p>

              <div v-if="change.changes && change.changes.length" class="sub-changes">
                <ul>
                  <li v-for="subChange in change.changes" :key="subChange.id">
                    <strong>{{ subChange.type }}:</strong>
                    {{ subChange.description }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- User Choice for Manual Migration -->
            <div v-if="migrationMode === 'manual'" class="change-actions">
              <div class="action-choice">
                <label>Actie voor dit product:</label>
                <SelectInput
                  :id="`action_${change.productId}`"
                  :model-value="getUserChoice(change.productId)"
                  @update:model-value="setUserChoice(change.productId, $event)"
                  :options="getActionOptions(change)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Migration Options -->
    <div class="migration-options">
      <h4>Migratie Opties</h4>

      <div class="option-cards">
        <div
          class="option-card"
          :class="{ 'option-card--selected': migrationMode === 'auto' }"
          @click="migrationMode = 'auto'"
        >
          <div class="option-header">
            <BaseIcon name="magic-wand" size="20" />
            <h5>Automatische Migratie</h5>
          </div>
          <p>Laat het systeem automatisch de wijzigingen toepassen.</p>
          <ul class="option-features">
            <li>✅ Snelle oplossing</li>
            <li>✅ Behoudt bestaande instellingen waar mogelijk</li>
            <li>⚠️ Verwijdert niet-beschikbare producten</li>
          </ul>
        </div>

        <div
          class="option-card"
          :class="{ 'option-card--selected': migrationMode === 'manual' }"
          @click="migrationMode = 'manual'"
        >
          <div class="option-header">
            <BaseIcon name="settings" size="20" />
            <h5>Handmatige Controle</h5>
          </div>
          <p>Kies per product hoe je de wijzigingen wilt afhandelen.</p>
          <ul class="option-features">
            <li>🎯 Volledige controle</li>
            <li>🔍 Bekijk elke wijziging</li>
            <li>⚙️ Aangepaste acties per product</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Auto Migration Settings -->
    <div v-if="migrationMode === 'auto'" class="auto-migration-settings">
      <h5>Automatische Migratie Instellingen</h5>
      <div class="settings-grid">
        <ToggleInput
          id="remove-deleted-products"
          label="Verwijder niet-beschikbare producten"
          v-model="autoMigrationOptions.removeDeletedProducts"
        />
        <ToggleInput
          id="update-existing-pages"
          label="Update bestaande pagina's"
          v-model="autoMigrationOptions.updateExistingPages"
        />
        <ToggleInput
          id="preserve-custom-settings"
          label="Behoud aangepaste instellingen"
          v-model="autoMigrationOptions.preserveCustomSettings"
        />
      </div>
    </div>

    <!-- Actions -->
    <div class="modal-actions">
      <Btn @click="handleCancel" variant="secondary" :disabled="migrating" type="button">
        Annuleren
      </Btn>

      <Btn @click="handleIgnore" variant="tertiary" :disabled="migrating" type="button">
        Negeren
      </Btn>

      <Btn
        @click="handleMigrate"
        variant="primary"
        :loading="migrating"
        :disabled="!canMigrate"
        type="button"
      >
        {{ migrationMode === 'auto' ? 'Automatisch Migreren' : 'Handmatig Migreren' }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted } from 'vue';
  import { useConfiguratorMigration } from '@/composables/useConfiguratorMigration';

  // Components
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';

  export default {
    name: 'ConfiguratorMigrationModal',

    components: {
      BaseIcon,
      Btn,
      SelectInput,
      ToggleInput,
    },

    props: {
      migrationData: {
        type: Object,
        required: true,
      },
    },

    emits: ['migrate', 'ignore', 'cancel'],

    setup(props, { emit }) {
      const { performAutoMigration, performManualMigration } = useConfiguratorMigration();

      const migrating = ref(false);
      const migrationMode = ref('auto');
      const userChoices = ref({});

      // Auto migration options
      const autoMigrationOptions = ref({
        removeDeletedProducts: true,
        updateExistingPages: true,
        preserveCustomSettings: true,
      });

      // Computed
      const configuratorName = computed(() => props.migrationData.configuratorName);

      const errorCount = computed(
        () => props.migrationData.changes.filter((c) => c.severity === 'error').length
      );

      const warningCount = computed(
        () => props.migrationData.changes.filter((c) => c.severity === 'warning').length
      );

      const canMigrate = computed(() => {
        if (migrationMode.value === 'auto') {
          return true;
        }

        // For manual mode, ensure all products have a choice
        return props.migrationData.changes.every((change) => userChoices.value[change.productId]);
      });

      // Methods
      const getChangeIcon = (type) => {
        const icons = {
          'product-removed': 'trash',
          'product-modified': 'edit',
          'product-error': 'exclamation-triangle',
        };
        return icons[type] || 'info-circle';
      };

      const getChangeTypeLabel = (type) => {
        const labels = {
          'product-removed': 'Product Verwijderd',
          'product-modified': 'Product Gewijzigd',
          'product-error': 'Fout',
        };
        return labels[type] || 'Onbekend';
      };

      const getActionOptions = (change) => {
        const baseOptions = [{ value: 'remove', label: 'Verwijder uit configurator' }];

        if (change.type === 'product-modified') {
          baseOptions.unshift(
            { value: 'keep', label: 'Behoud huidige versie' },
            { value: 'regenerate', label: 'Update naar nieuwe versie' }
          );
        }

        return baseOptions;
      };

      const getUserChoice = (productId) => {
        return userChoices.value[productId] || '';
      };

      const setUserChoice = (productId, choice) => {
        userChoices.value[productId] = choice;
      };

      const handleMigrate = async () => {
        try {
          migrating.value = true;

          let result;
          if (migrationMode.value === 'auto') {
            result = await performAutoMigration(
              props.migrationData.configuratorId,
              autoMigrationOptions.value
            );
          } else {
            const choices = Object.entries(userChoices.value).map(([productId, action]) => ({
              productId,
              action,
            }));
            result = await performManualMigration(props.migrationData.configuratorId, choices);
          }

          emit('migrate', result);
        } catch (error) {
          console.error('Migration failed:', error);
          emit('migrate', { success: false, error: error.message });
        } finally {
          migrating.value = false;
        }
      };

      const handleIgnore = () => {
        emit('ignore');
      };

      const handleCancel = () => {
        emit('cancel');
      };

      // Initialize default choices for manual mode
      onMounted(() => {
        props.migrationData.changes.forEach((change) => {
          if (change.type === 'product-modified') {
            userChoices.value[change.productId] = 'regenerate';
          } else if (change.type === 'product-removed') {
            userChoices.value[change.productId] = 'remove';
          }
        });
      });

      return {
        // State
        migrating,
        migrationMode,
        userChoices,
        autoMigrationOptions,

        // Computed
        configuratorName,
        errorCount,
        warningCount,
        canMigrate,

        // Methods
        getChangeIcon,
        getChangeTypeLabel,
        getActionOptions,
        getUserChoice,
        setUserChoice,
        handleMigrate,
        handleIgnore,
        handleCancel,
      };
    },
  };
</script>

<style scoped lang="scss">
  .configurator-migration-modal {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    max-height: 80vh;
    overflow-y: auto;
  }

  .migration-header {
    display: flex;
    gap: 1rem;
    align-items: flex-start;

    .header-icon {
      flex-shrink: 0;
      width: 48px;
      height: 48px;
      background: var(--brand-100);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--brand-600);
    }

    .header-content {
      flex: 1;

      h3 {
        margin: 0 0 0.5rem 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--slate-800);
      }

      p {
        margin: 0;
        color: var(--slate-600);
        line-height: 1.5;
      }
    }
  }

  .migration-summary {
    .summary-stats {
      display: flex;
      gap: 2rem;

      .stat-item {
        text-align: center;

        .stat-number {
          display: block;
          font-size: 2rem;
          font-weight: 700;
          color: var(--slate-800);

          &.error {
            color: var(--red-600);
          }

          &.warning {
            color: var(--amber-600);
          }
        }

        .stat-label {
          display: block;
          font-size: 0.875rem;
          color: var(--slate-500);
          margin-top: 0.25rem;
        }
      }
    }
  }

  .changes-detail {
    h4 {
      margin: 0 0 1rem 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    .changes-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .change-item {
      display: flex;
      gap: 1rem;
      padding: 1rem;
      border-radius: 8px;
      border: 1px solid var(--slate-200);

      &--error {
        border-color: var(--red-200);
        background: var(--red-50);
      }

      &--warning {
        border-color: var(--amber-200);
        background: var(--amber-50);
      }

      .change-icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--slate-100);

        .icon--error {
          color: var(--red-600);
        }

        .icon--warning {
          color: var(--amber-600);
        }
      }

      .change-content {
        flex: 1;

        .change-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;

          h5 {
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
            color: var(--slate-800);
          }

          .change-type {
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            background: var(--slate-100);
            color: var(--slate-600);
            font-weight: 500;
          }
        }

        .change-description {
          p {
            margin: 0 0 0.5rem 0;
            color: var(--slate-600);
            font-size: 0.875rem;
          }

          .sub-changes {
            ul {
              margin: 0;
              padding-left: 1rem;

              li {
                font-size: 0.875rem;
                color: var(--slate-600);
                margin-bottom: 0.25rem;
              }
            }
          }
        }

        .change-actions {
          margin-top: 1rem;

          .action-choice {
            label {
              display: block;
              font-size: 0.875rem;
              font-weight: 500;
              color: var(--slate-700);
              margin-bottom: 0.5rem;
            }
          }
        }
      }
    }
  }

  .migration-options {
    h4 {
      margin: 0 0 1rem 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    .option-cards {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }

    .option-card {
      padding: 1.5rem;
      border: 2px solid var(--slate-200);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: var(--brand-300);
      }

      &--selected {
        border-color: var(--brand-500);
        background: var(--brand-50);
      }

      .option-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.75rem;

        h5 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--slate-800);
        }
      }

      p {
        margin: 0 0 1rem 0;
        font-size: 0.875rem;
        color: var(--slate-600);
        line-height: 1.4;
      }

      .option-features {
        list-style: none;
        margin: 0;
        padding: 0;

        li {
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }
      }
    }
  }

  .auto-migration-settings {
    padding: 1rem;
    background: var(--slate-50);
    border-radius: 6px;

    h5 {
      margin: 0 0 1rem 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    .settings-grid {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-top: 1rem;
    border-top: 1px solid var(--slate-200);
  }

  @media (max-width: 768px) {
    .option-cards {
      grid-template-columns: 1fr;
    }

    .summary-stats {
      flex-direction: column;
      gap: 1rem !important;
    }

    .modal-actions {
      flex-direction: column;
    }
  }
</style>
